/**
 * Email auto-status (post-application). JobCork puts a relay address it owns on
 * every application it sends -- reading like the person, steven.foster.k7q2
 * @jobcork.com -- so each employer reply is filed under the right application,
 * moves its stage on its own, and lands in the person's real inbox a moment
 * later. Nothing to set up. This screen shows the address and what has come in.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";

interface Update {
  subject: string | null;
  fromName: string | null;
  toAddr: string | null;
  /** "in" — an employer wrote; "out" — you sent a follow-up from your address. */
  direction: "in" | "out";
  intent: string;
  movedTo: string | null;
  receivedAt: string;
  title: string | null;
  employer: string | null;
}
interface Intake {
  address: string;
  updates: Update[];
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function line(u: Update): string {
  if (u.direction === "out") {
    const to = u.employer ?? u.toAddr ?? "the recruiter";
    return `You followed up — ${to}`;
  }
  const who = u.employer ?? u.fromName ?? "A reply";
  if (u.movedTo) return `${who} — moved to ${u.movedTo}`;
  if (u.intent === "unmatched") return `${who} — couldn't match it to an application`;
  return `${who} — replied`;
}

export function EmailUpdates() {
  const [data, setData] = useState<Intake | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api<Intake>("/api/email-intake").then(setData).catch((e) => setError(describe(e)));
  }, []);

  if (!data && !error) return null;

  const copy = () => {
    if (!data) return;
    navigator.clipboard.writeText(data.address)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
      .catch(() => { /* clipboard blocked; the address is selectable */ });
  };

  return (
    <section>
      <h2>Email updates</h2>
      {error && <p className="err small">{error}</p>}
      {data && (
        <>
          <p className="muted small">
            JobCork applies with your application email below, so every employer reply lands in your inbox
            <em> and</em> updates the application on its own — an interview invite moves it to interviewing, an offer
            to offer, a rejection to rejected; plain replies are logged here. Nothing to set up. Reply to any of
            them from your inbox as usual, and it goes straight to the employer.
          </p>
          <div className="intake-addr">
            <code>{data.address}</code>
            <button className="btn ghost small" type="button" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
          </div>
          <p className="muted small">
            Applied somewhere yourself? Use this address on that application, or forward the employer's reply to it, and JobCork files it the same way.
          </p>
          {data.updates.length > 0 ? (
            <div className="match-preview">
              {data.updates.map((u, i) => (
                <div className="mp-row" key={i}>
                  <div className="mp-main">
                    <div><strong>{line(u)}</strong></div>
                    {u.subject && <div className="muted small">{u.subject}</div>}
                  </div>
                  <span className="muted small">{when(u.receivedAt)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted small">No employer replies yet — the first one shows up here and moves the application on its own.</p>
          )}
        </>
      )}
    </section>
  );
}
